import React from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, FilePen, FileX, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"

import type { Tables } from "../../../database.types"

interface DocumentVerificationProps {
  isOpen: boolean
  onClose: () => void
  document: Tables<"documents">
}

// Custom hook for updating document status
const useUpdateDocumentStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      applicationId,
      documentName,
      status,
    }: {
      applicationId: string
      documentName: string
      status: string
    }) => {
      if (!applicationId) {
        throw new Error("Application ID is required")
      }

      const { data, error } = await supabase
        .from("documents")
        .update({ status })
        .eq("application_id", applicationId)
        .eq("type", documentName)

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: (_, variables) => {
      // Invalidate the application details query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["applicationDetails", variables.applicationId],
      })
    },
  })
}

const DocumentVerification = ({ isOpen, onClose, document }: DocumentVerificationProps) => {
  const { user } = useAuth()
  const updateDocumentStatus = useUpdateDocumentStatus()

  const handleVerify = () => {
    if (!document.application_id) {
      toast.error("Error", {
        description: "Application ID is missing",
      })
      return
    }

    updateDocumentStatus.mutate(
      {
        applicationId: document.application_id,
        documentName: document.type,
        status: "Verified",
      },
      {
        onSuccess: () => {
          toast.success("Document Verified", {
            description: `${document.type} has been verified successfully`,
          })
          onClose()
        },
        onError: (error) => {
          console.error("Error updating document status:", error)
          toast.error("Error", {
            description: "Failed to update document status",
          })
        },
      },
    )
  }

  const handleReject = () => {
    if (!document.application_id) {
      toast.error("Error", {
        description: "Application ID is missing",
      })
      return
    }

    updateDocumentStatus.mutate(
      {
        applicationId: document.application_id,
        documentName: document.type,
        status: "Rejected",
      },
      {
        onSuccess: () => {
          toast.error("Document Rejected", {
            description: `${document.type} has been rejected`,
          })
          onClose()
        },
        onError: (error) => {
          console.error("Error updating document status:", error)
          toast.error("Error", {
            description: "Failed to update document status",
          })
        },
      },
    )
  }

  const handlePending = () => {
    if (!document.application_id) {
      toast.error("Error", {
        description: "Application ID is missing",
      })
      return
    }

    updateDocumentStatus.mutate(
      {
        applicationId: document.application_id,
        documentName: document.type,
        status: "Pending",
      },
      {
        onSuccess: () => {
          toast.success("Document Status Updated", {
            description: `${document.type} has been marked as pending`,
          })
          onClose()
        },
        onError: (error) => {
          console.error("Error updating document status:", error)
          toast.error("Error", {
            description: "Failed to update document status",
          })
        },
      },
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Verify Document: {document.type}</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="flex flex-col items-center space-y-4">
            {document.file ? (
              <div className="w-full max-h-[600px] overflow-auto border border-palette-jungleGreen200 rounded-md p-1">
                {document.file.split("?")[0].toLowerCase().endsWith(".pdf") ? (
                  <iframe
                    src={`${document.file}#toolbar=0`}
                    title={document.type}
                    className="w-full h-[600px]"
                  />
                ) : (
                  <img src={document.file} alt={document.type} className="w-full object-contain" />
                )}
              </div>
            ) : (
              <div className="w-full h-[250px] flex items-center justify-center bg-palette-jungleGreen100 rounded-md">
                <p className="text-palette-jungleGreen500">No document preview available</p>
              </div>
            )}

            <div className="w-full bg-palette-jungleGreen000 p-4 rounded-md">
              <p className="font-medium">Document Information:</p>
              <p className="text-sm text-palette-jungleGreen500 mt-2">Name: {document.type}</p>
              <p className="text-sm text-palette-jungleGreen500">
                Current Status:
                <span
                  className={`ml-1 font-medium ${
                    document.status === "Verified"
                      ? "text-green-600"
                      : document.status === "Rejected"
                        ? "text-red-600"
                        : "text-yellow-600"
                  }`}
                >
                  {document.status}
                </span>
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="outline" onClick={onClose} disabled={updateDocumentStatus.isPending}>
            Close
          </Button>
          {/* Hide action buttons for DSP users */}
          {user?.role !== "DSP" && (
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={handleReject}
                className="flex items-center gap-1"
                disabled={updateDocumentStatus.isPending}
              >
                {updateDocumentStatus.isPending &&
                updateDocumentStatus.variables?.status === "Rejected" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileX className="h-4 w-4" />
                )}
                Reject
              </Button>
              <Button
                variant="outline"
                onClick={handlePending}
                className="flex items-center gap-1"
                disabled={updateDocumentStatus.isPending}
              >
                {updateDocumentStatus.isPending &&
                updateDocumentStatus.variables?.status === "Pending" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FilePen className="h-4 w-4" />
                )}
                Mark Pending
              </Button>
              <Button
                variant="default"
                onClick={handleVerify}
                className="flex items-center gap-1"
                disabled={updateDocumentStatus.isPending}
              >
                {updateDocumentStatus.isPending &&
                updateDocumentStatus.variables?.status === "Verified" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Verify
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentVerification
